import React from 'react';
import { useParams } from 'react-router-dom';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DEFAULT_FORMAT, DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { orderKindOptions, orderStateOptions, paymentMethodOptions } from '@constants/masterData';
import useListModal from '@hooks/useListModal';
import useRestaurant from '@hooks/useRestaurant';
import useTranslate from '@hooks/useTranslate';
import { BaseTable } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { convertUtcToLocalTime, formatMoney } from '@utils';
import { Tag } from 'antd';

const BillingListPage = () => {
    const translate = useTranslate();
    const { restaurantId } = useParams();
    const { currentSettingParse } = useRestaurant();
    const stateValue = translate.formatKeys(orderStateOptions, ['label']);
    const paymentMethodValue = translate.formatKeys(paymentMethodOptions, ['label']);

    const {
        data,
        mixinFuncs,
        queryFilter,
        loading,
        pagination,
    } = useListModal({
        apiConfig: apiConfig.billing,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(commonMessage.billing),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            const originalPrepareGetListParams = funcs.prepareGetListParams;
            funcs.prepareGetListParams = (params) => {
                const newParams = originalPrepareGetListParams(params);
                return {
                    ...newParams,
                    restaurantId: restaurantId,
                };
            };
        },
    });

    const columns = [
        {
            title: translate.formatMessage(commonMessage.createdDate),
            dataIndex: 'createdDate',
            width: '180px',
            render: (createdDate) => {
                const createdDateLocal = convertUtcToLocalTime(createdDate, DEFAULT_FORMAT, DEFAULT_FORMAT);
                return <div>{createdDateLocal}</div>;
            },
        },
        {
            title: translate.formatMessage(commonMessage.orderStt),
            dataIndex: 'orderStt',
            width: '140px',
        },
        {
            title: translate.formatMessage(commonMessage.customerName),
            dataIndex: ['customerInfo'],
            render: (customerInfo) => {
                try {
                    const customerInfoObj = JSON.parse(customerInfo);
                    return <div>{customerInfoObj?.userName}</div>;
                } catch (error) {
                    return <div>--</div>;
                }
            },
        },
        {
            title: translate.formatMessage(commonMessage.deliveryFee),
            dataIndex: 'shipMoney',
            width: '180px',
            align: 'center',
            render: (shipMoney) => {
                return formatMoney(shipMoney || 0, currentSettingParse);
            },
        },
        {
            title: translate.formatMessage(commonMessage.totalMoney),
            dataIndex: 'money',
            width: '180px',
            align: 'center',
            render: (money, record) => {
                const shipMoney = record.shipMoney || 0;
                const total = (money / 100) + shipMoney;
                return formatMoney(total, currentSettingParse);
            },
        },
        {
            title: translate.formatMessage(commonMessage.orderType),
            dataIndex: 'kind',
            width: '180px',
            render: (kind) => {
                const currentState = orderKindOptions?.find(item => item.value === kind);

                return currentState?.label;
            },
        },
        {
            title: translate.formatMessage(commonMessage.payment),
            dataIndex: 'paymentMethod',
            width: '200px',
            render: (paymentMethod) => {
                const currentPayment = paymentMethodValue?.find(item => item.value === paymentMethod);
                return <span>{currentPayment?.label}</span>;
            },
        },
        // {
        //     title: translate.formatMessage(commonMessage.state),
        //     dataIndex: 'state',
        //     width: '120px',
        //     align: 'center',
        //     render: (state) => {
        //         const currentState = stateValue?.find(item => item.value === state);

        //         return (
        //             <Tag color={currentState?.color}>
        //                 <div style={{ padding: '0 4px', fontSize: 14 }}>{currentState?.label}</div>
        //             </Tag>
        //         );
        //     },
        // },
    ];

    const searchFields = [
        {
            key: 'kind',
            placeholder: translate.formatMessage(commonMessage.orderType),
            type: FieldTypes.SELECT,
            options: orderKindOptions,
            submitOnChanged: true,
            colSpan: 5,
        },
    ];

    return (
        <PageWrapper routes={[{ breadcrumbName: translate.formatMessage(commonMessage.billing) }]}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields, initialValues: queryFilter })}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        pagination={pagination}
                        style={{ cursor: 'pointer' }}
                        loading={loading}
                    />
                }
            />
        </PageWrapper>
    );
};

export default BillingListPage;